import { useState, useEffect } from 'react';
import { FiTrash2, FiSearch, FiBookmark } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import storageService from '../../services/storageService';

/**
 * Componente para gestionar subrayados de versículos
 * - Usuario registrado: guarda localmente (IndexedDB)
 * - Lector con permisos: puede sincronizar con backend
 */
export default function BibleHighlights() {
  const { user } = useAuth();
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedColor, setSelectedColor] = useState('all');

  const colors = [
    { name: 'Amarillo', value: '#FEF3C7', label: '💛 Amarillo' },
    { name: 'Verde', value: '#D1FAE5', label: '💚 Verde' },
    { name: 'Azul', value: '#DBEAFE', label: '💙 Azul' },
    { name: 'Naranja', value: '#FED7AA', label: '🧡 Naranja' },
    { name: 'Morado', value: '#E9D5FF', label: '💜 Morado' },
    { name: 'Rosa', value: '#FBCFE8', label: '💗 Rosa' }
  ];

  useEffect(() => {
    if (user) {
      loadHighlights();
    }
  }, [user]);

  const loadHighlights = async () => {
    setLoading(true);
    try {
      await storageService.init();
      // Filtrar por usuario actual
      const allHighlights = user?.id 
        ? await storageService.bibleHighlights.obtenerPorUsuario(user.id)
        : await storageService.bibleHighlights.obtenerTodos();
      console.log('🎨 Subrayados cargados:', allHighlights.length);
      setHighlights(allHighlights);
    } catch (error) {
      console.error('Error cargando subrayados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este subrayado?')) return;

    try {
      await storageService.bibleHighlights.eliminar(id);
      console.log('✅ Subrayado eliminado');
      await loadHighlights();
    } catch (error) {
      console.error('Error eliminando subrayado:', error);
      alert('Error al eliminar el subrayado');
    }
  };

  const filteredHighlights = highlights.filter(highlight => {
    const matchesSearch = !searchTerm || 
      highlight.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      highlight.text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      highlight.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesColor = selectedColor === 'all' || highlight.color === selectedColor;
    
    return matchesSearch && matchesColor;
  });

  // Agrupar por color
  const highlightsByColor = filteredHighlights.reduce((acc, h) => {
    const colorName = colors.find(c => c.value === h.color)?.name || 'Otro';
    if (!acc[colorName]) acc[colorName] = [];
    acc[colorName].push(h);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FiBookmark className="text-blue-600" />
          Mis Subrayados
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {highlights.length} {highlights.length === 1 ? 'versículo subrayado' : 'versículos subrayados'}
        </p>
      </div>

      {/* Nota informativa sobre cómo crear subrayados */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>💡 Cómo subrayar versículos:</strong> Vaya al "Lector" → Seleccione uno o varios versículos → 
          Use el botón de subrayado que aparece → Elija un color. Los subrayados se guardarán aquí automáticamente.
        </p>
      </div>

      {/* Filtros */}
      {highlights.length > 0 && (
        <div className="mb-4 space-y-4">
          {/* Búsqueda */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por referencia o texto..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filtro por color */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedColor('all')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedColor === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
            {colors.map((color) => (
              <button
                key={color.value}
                onClick={() => setSelectedColor(color.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedColor === color.value
                    ? 'ring-2 ring-blue-600'
                    : 'hover:ring-2 hover:ring-gray-300'
                }`}
                style={{ backgroundColor: color.value }}
              >
                {color.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Lista de subrayados */}
      {filteredHighlights.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {highlights.length === 0 ? (
            <>
              <FiBookmark className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">📭 No tienes subrayados</p>
              <p className="text-sm">Ve al "Lector" y selecciona versículos para subrayarlos</p>
            </>
          ) : (
            <>
              <p className="text-lg mb-2">🔍 No se encontraron subrayados</p>
              <p className="text-sm">Intenta con otros términos de búsqueda o filtros</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(highlightsByColor).map(([colorName, colorHighlights]) => (
            <div key={colorName}>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: colorHighlights[0].color }}
                />
                {colorName} ({colorHighlights.length})
              </h3>

              <div className="space-y-3">
                {colorHighlights.map((highlight) => (
                  <div
                    key={highlight.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    style={{ backgroundColor: `${highlight.color}40` }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-blue-600 bg-white px-2 py-1 rounded">
                            {highlight.reference || `${highlight.book} ${highlight.chapter}:${highlight.verseStart}${highlight.verseEnd !== highlight.verseStart ? `-${highlight.verseEnd}` : ''}`}
                          </span>
                          {highlight.version && (
                            <span className="text-xs text-gray-600">
                              ({highlight.version})
                            </span>
                          )}
                          <div 
                            className="w-6 h-6 rounded border-2 border-gray-300"
                            style={{ backgroundColor: highlight.color }}
                            title={`Color: ${highlight.color}`}
                          />
                        </div>

                        {highlight.text && (
                          <p className="text-gray-800 italic mb-2">
                            "{highlight.text}"
                          </p>
                        )}

                        {highlight.notes && (
                          <div className="bg-white rounded p-2 text-sm text-gray-700">
                            <strong>Nota:</strong> {highlight.notes}
                          </div>
                        )}

                        {highlight.createdAt && (
                          <div className="text-xs text-gray-500 mt-2">
                            Subrayado el {new Date(highlight.createdAt).toLocaleString('es-ES')}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => handleDelete(highlight.id)}
                        className="p-2 text-red-600 hover:bg-white rounded-lg ml-2"
                        title="Eliminar"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Nota informativa */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>💡 Tip:</strong> Los subrayados se guardan localmente en tu navegador. Puedes organizarlos por colores 
          para diferentes temas de estudio (ej: amarillo=profecía, verde=promesas, azul=doctrina).
        </p>
      </div>
    </div>
  );
}


